/** Lado largo máximo (px): suficiente para avatares en UI. */
const MAX_LONG_EDGE_PX = 1024;
/** Tope del archivo que se envía al API tras comprimir. */
const MAX_FILE_BYTES = 1.5 * 1024 * 1024;

function loadImageElement(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("IMAGE_LOAD"));
    };
    img.src = url;
  });
}

function canvasToJpegBlob(
  canvas: HTMLCanvasElement,
  quality: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("TO_BLOB"))),
      "image/jpeg",
      quality
    );
  });
}

function toJpegFile(blob: Blob, originalName: string): File {
  const baseName = originalName.replace(/\.[^.]+$/, "") || "avatar";
  return new File([blob], `${baseName}.jpg`, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

/**
 * Escala y comprime el avatar a JPEG liviano antes de subirlo.
 * Permite seleccionar fotos grandes (p. ej. del celular) sin romper el límite del API.
 */
export async function prepareAvatarFile(file: File): Promise<File> {
  if (!file.type.startsWith("image/")) {
    return file;
  }

  const img = await loadImageElement(file);
  const sw = img.naturalWidth || 1;
  const sh = img.naturalHeight || 1;
  const longEdge = Math.max(sw, sh);
  const alreadyFits =
    file.size <= MAX_FILE_BYTES &&
    longEdge <= MAX_LONG_EDGE_PX &&
    (file.type === "image/jpeg" || file.type === "image/jpg");

  if (alreadyFits) {
    return file;
  }

  let scale = Math.min(1, MAX_LONG_EDGE_PX / longEdge);
  let cw = Math.max(1, Math.round(sw * scale));
  let ch = Math.max(1, Math.round(sh * scale));

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return file;
  }
  canvas.width = cw;
  canvas.height = ch;
  ctx.drawImage(img, 0, 0, cw, ch);

  let quality = 0.9;
  for (let i = 0; i < 14; i++) {
    const blob = await canvasToJpegBlob(canvas, quality);
    if (blob.size <= MAX_FILE_BYTES) {
      return toJpegFile(blob, file.name);
    }
    quality -= 0.05;
    if (quality < 0.4) break;
  }

  let factor = 0.88;
  while (factor > 0.28) {
    cw = Math.max(1, Math.round(cw * factor));
    ch = Math.max(1, Math.round(ch * factor));
    canvas.width = cw;
    canvas.height = ch;
    ctx.drawImage(img, 0, 0, cw, ch);
    for (let q = 0.88; q >= 0.4; q -= 0.05) {
      const blob = await canvasToJpegBlob(canvas, q);
      if (blob.size <= MAX_FILE_BYTES) {
        return toJpegFile(blob, file.name);
      }
    }
    factor -= 0.07;
  }

  const last = await canvasToJpegBlob(canvas, 0.4);
  return toJpegFile(last, file.name);
}
